/**
 * HeroModePreview — polished miniature mockups of every hero mode.
 *
 * Replaces the previous abstract geometric icons (flat ``currentColor``
 * rectangles) with richer SVG illustrations that read as tiny
 * renderings of real heroes:
 *
 *   * Photo-bearing slots use vertical / radial gradients (blue→slate)
 *     to mimic actual imagery rather than a flat tint, with a subtle
 *     inner shadow line for depth.
 *   * Text bars use ``currentColor`` so they remain theme-aware (dark
 *     in light mode, light in dark mode), with size + opacity steps
 *     that mirror real heading / body / CTA hierarchy.
 *   * Each preview renders its own ``<defs>`` block of gradients
 *     (gradient ids are namespaced with the mode id to avoid collisions
 *     when multiple previews mount simultaneously).
 *
 * The picker UI calls ``<HeroModePreview modeId="cinematic" />`` and
 * receives an SVG sized to the parent container. Animation indicators
 * (the "Animated" chip) are layered separately on top by the picker
 * — this file only owns the static visual identity of each mode.
 *
 * Adding a new mode
 * -----------------
 * 1. Add a renderer entry in ``PREVIEW_RENDERERS`` keyed by the mode id.
 * 2. Use ``${modeId}-grad`` as the namespace prefix for any gradients
 *    you define inside ``<defs>`` so they never collide with sibling
 *    previews mounted in the same DOM tree.
 * 3. Stick to the 24×16 viewBox so the proportions match the picker
 *    tile aspect ratio (the wrapper sets ``preserveAspectRatio="none"``
 *    is NOT used — proportions are real, not stretched).
 *
 * If a mode has no preview defined, ``<HeroModePreview>`` falls back to
 * a single-letter monogram in the picker tile.
 */
import React from "react";
import { textColors } from "../../../design-system/tokens";

// ---------------------------------------------------------------------------
// Shared palette (all modes use these so the picker reads as one family)
// ---------------------------------------------------------------------------
//
// Imagery is rendered with cool blue-to-slate gradients so a "photo
// placeholder" stays visually distinct from a "text bar" without
// having to caption it. The colors are chosen to read fine over both
// light and dark picker surfaces — opacity is layered on top of the
// color, not used as the only contrast mechanism.

const IMG_GRADIENT_TOP = "#60a5fa";    // blue-400 — photo highlight
const IMG_GRADIENT_BOTTOM = "#1e293b"; // slate-800 — photo shadow
const IMG_ACCENT_TOP = "#a78bfa";      // violet-400 — accent imagery
const IMG_ACCENT_BOTTOM = "#312e81";   // indigo-900 — accent shadow

/**
 * Reusable gradient defs for a "photo" surface.
 *
 * Returns two stops (top → bottom) that read like a real cool-toned
 * photograph. ``id`` must be globally unique within the rendered SVG
 * tree, so callers should namespace with the mode id (e.g.
 * ``cinematic-photo``).
 */
function PhotoGradient({ id, accent = false }) {
  const top = accent ? IMG_ACCENT_TOP : IMG_GRADIENT_TOP;
  const bottom = accent ? IMG_ACCENT_BOTTOM : IMG_GRADIENT_BOTTOM;
  return (
    <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor={top} stopOpacity="0.85" />
      <stop offset="100%" stopColor={bottom} stopOpacity="0.85" />
    </linearGradient>
  );
}

/**
 * Vertical fade gradient for the soft-edge Split variants. Goes from
 * the photo color (left side) to fully transparent (right side) so the
 * preview literally renders the fade the mode produces.
 */
function HorizontalFadeGradient({ id, fromLeft = true, accent = false }) {
  const top = accent ? IMG_ACCENT_TOP : IMG_GRADIENT_TOP;
  const bottom = accent ? IMG_ACCENT_BOTTOM : IMG_GRADIENT_BOTTOM;
  // x1/x2 controls direction of fade — fromLeft means opaque on the
  // LEFT, transparent on the RIGHT (used for ``split_fade``); the
  // mirror is used for ``split_fade_right``.
  const [x1, x2] = fromLeft ? ["0%", "100%"] : ["100%", "0%"];
  return (
    <linearGradient id={id} x1={x1} y1="0%" x2={x2} y2="0%">
      <stop offset="0%" stopColor={top} stopOpacity="0.8" />
      <stop offset="55%" stopColor={bottom} stopOpacity="0.65" />
      <stop offset="100%" stopColor={bottom} stopOpacity="0" />
    </linearGradient>
  );
}

// ---------------------------------------------------------------------------
// Mode-specific preview renderers
// ---------------------------------------------------------------------------
//
// Each renderer returns a fragment of SVG content rendered inside a
// 24×16 viewBox. The picker frame already supplies the outer rounded
// border + background; renderers only own the *content* (photos, text
// bars, decorative elements). Gradient ids must be namespaced with
// the mode id to avoid collisions when multiple previews mount.

const PREVIEW_RENDERERS = {
  // ------------------------------------------------------------------
  // Cinematic family
  // ------------------------------------------------------------------
  cinematic: (
    <>
      <defs>
        <PhotoGradient id="cinematic-photo" />
      </defs>
      {/* Full-bleed photo background */}
      <rect x="0" y="0" width="24" height="16" fill="url(#cinematic-photo)" />
      {/* Vignette in bottom-left to anchor text */}
      <rect x="0" y="6" width="14" height="10" fill="#0f172a" opacity="0.3" />
      {/* Title (bottom-left, larger, white) */}
      <rect x="1.6" y="9.5" width="9" height="1.6" rx="0.4" fill="#ffffff" opacity="0.95" />
      {/* Subtitle */}
      <rect x="1.6" y="11.7" width="6" height="1" rx="0.3" fill="#ffffff" opacity="0.7" />
      {/* CTA pill */}
      <rect x="1.6" y="13.4" width="3.5" height="1.4" rx="0.7" fill="#ffffff" opacity="0.85" />
    </>
  ),
  cinematic_right: (
    <>
      <defs>
        <PhotoGradient id="cinematic_right-photo" />
      </defs>
      <rect x="0" y="0" width="24" height="16" fill="url(#cinematic_right-photo)" />
      {/* Vignette mirrored to bottom-right */}
      <rect x="10" y="6" width="14" height="10" fill="#0f172a" opacity="0.3" />
      <rect x="13.4" y="9.5" width="9" height="1.6" rx="0.4" fill="#ffffff" opacity="0.95" />
      <rect x="16.4" y="11.7" width="6" height="1" rx="0.3" fill="#ffffff" opacity="0.7" />
      <rect x="18.9" y="13.4" width="3.5" height="1.4" rx="0.7" fill="#ffffff" opacity="0.85" />
    </>
  ),

  // ------------------------------------------------------------------
  // Split family
  // ------------------------------------------------------------------
  split: (
    <>
      <defs>
        <PhotoGradient id="split-photo" />
      </defs>
      {/* Image half on the left */}
      <rect x="0" y="0" width="11" height="16" fill="url(#split-photo)" />
      {/* Subtle inner highlight to suggest a horizon */}
      <rect x="0" y="9" width="11" height="0.3" fill="#ffffff" opacity="0.18" />
      {/* Text column on the right (text uses currentColor so it stays theme-aware) */}
      <rect x="13" y="3.5" width="8" height="1.8" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="13" y="6.3" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="13" y="8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="13" y="9.7" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      {/* CTA pill */}
      <rect x="13" y="12" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
    </>
  ),
  split_right: (
    <>
      <defs>
        <PhotoGradient id="split_right-photo" />
      </defs>
      {/* Text column on the left (right-aligned to lean into the image) */}
      <rect x="2" y="3.5" width="8" height="1.8" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1" y="6.3" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="3" y="8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="5" y="9.7" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="6.2" y="12" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Image half on the right */}
      <rect x="13" y="0" width="11" height="16" fill="url(#split_right-photo)" />
      <rect x="13" y="9" width="11" height="0.3" fill="#ffffff" opacity="0.18" />
    </>
  ),
  split_fade: (
    <>
      <defs>
        {/* Image fades from solid (left edge) to transparent (toward
            the seam) — the literal effect the renderer applies via a
            CSS mask gradient. */}
        <HorizontalFadeGradient id="split_fade-photo" fromLeft={true} />
      </defs>
      {/* Image half painted with the fade gradient (no hard inner edge) */}
      <rect x="0" y="0" width="14" height="16" fill="url(#split_fade-photo)" />
      {/* Text column — sits slightly further right because the image
          dissolves rather than ending in a hard line. */}
      <rect x="14" y="3.5" width="8" height="1.8" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="14" y="6.3" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="14" y="8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="14" y="9.7" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="14" y="12" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
    </>
  ),
  split_fade_right: (
    <>
      <defs>
        <HorizontalFadeGradient id="split_fade_right-photo" fromLeft={false} />
      </defs>
      {/* Text column on the left — same content shape as split_right */}
      <rect x="2" y="3.5" width="8" height="1.8" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1" y="6.3" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="3" y="8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="5" y="9.7" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="6.2" y="12" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Image half on the right with mirrored fade (opaque on the
          right edge, dissolving toward the inner seam). */}
      <rect x="10" y="0" width="14" height="16" fill="url(#split_fade_right-photo)" />
    </>
  ),
  // Visitor-driven Split sibling. The mockup deliberately renders
  // the *expanded* state (image wider than 50%, text column
  // compressed) with a small cursor glyph hovering inside the
  // image so the picker tile communicates "the photo widens
  // because the visitor is hovering it" at a single glance — same
  // composition signal ``photo_banner_hover_focus`` uses for its
  // own visitor-driven width-breathing identity. The cursor is
  // drawn as a self-contained polygon (no font icon dependency)
  // and tinted with currentColor so it inherits the picker's
  // foreground in both light and dark themes.
  split_hover_expand: (
    <>
      <defs>
        <PhotoGradient id="split_hover_expand-photo" />
      </defs>
      {/* Image half on the left, drawn at the *expanded* width
          (~16/24 ≈ 67%) so the tile reads as "this photo can
          take over more of the hero". */}
      <rect x="0" y="0" width="16" height="16" fill="url(#split_hover_expand-photo)" />
      {/* Subtle inner highlight to suggest a horizon — same trick
          the canonical ``split`` preview uses so siblings read as
          one family. */}
      <rect x="0" y="9" width="16" height="0.3" fill="#ffffff" opacity="0.18" />
      {/* Cursor arrow sitting inside the photo to communicate the
          hover affordance — same currentColor-stroked-on-white
          glyph the ``photo_banner_hover_focus`` /
          ``gallery_proximity`` previews use. Positioned roughly
          centered on the image so it reads as "the visitor is
          hovering the photo right now". */}
      <path
        d="M 7.5 6 L 7.5 10 L 8.6 9 L 9.4 10.7 L 10.1 10.4 L 9.3 8.7 L 10.7 8.7 Z"
        fill="#ffffff"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="0.15"
      />
      {/* Text column on the right — compressed to match the
          expanded image. Same currentColor / opacity scale as the
          canonical ``split`` preview so the visual rhythm reads
          as "same family, different width split". */}
      <rect x="17" y="3.5" width="6" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="17" y="6.3" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="17" y="8" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="17" y="9.7" width="4" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="17" y="12" width="3.5" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Centered family — typography-first, no imagery
  // ------------------------------------------------------------------
  centered: (
    <>
      <defs>
        <linearGradient id="centered-bg" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f1f5f9" stopOpacity="0" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      {/* Faint surface gradient just to give the preview a bit of
          warmth — mode itself is content-only. */}
      <rect x="0" y="0" width="24" height="16" fill="url(#centered-bg)" />
      {/* Centred title */}
      <rect x="6.5" y="4.5" width="11" height="2" rx="0.4" fill="currentColor" opacity="0.95" />
      {/* Subtitle */}
      <rect x="7.5" y="7.4" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="8.5" y="9" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      {/* CTA */}
      <rect x="9.5" y="11.5" width="5" height="1.6" rx="0.8" fill="currentColor" opacity="0.7" />
    </>
  ),
  centered_animated: null, // resolved by family reuse — see resolveBaseModeId

  // ------------------------------------------------------------------
  // Card family — frosted card overlay
  // ------------------------------------------------------------------
  card: (
    <>
      <defs>
        <PhotoGradient id="card-bg" />
      </defs>
      {/* Photo-ish background */}
      <rect x="0" y="0" width="24" height="16" fill="url(#card-bg)" />
      {/* Floating frosted card */}
      <rect x="3.5" y="2.8" width="17" height="10.4" rx="1.2" fill="#ffffff" opacity="0.92" />
      <rect x="3.5" y="2.8" width="17" height="10.4" rx="1.2" fill="none" stroke="#ffffff" strokeWidth="0.4" opacity="0.9" />
      {/* Card content (text uses dark slate so it reads on the white card) */}
      <rect x="5.3" y="4.8" width="10" height="1.6" rx="0.4" fill="#0f172a" opacity="0.85" />
      <rect x="6.3" y="7" width="8" height="1" rx="0.3" fill="#0f172a" opacity="0.45" />
      <rect x="7.3" y="8.6" width="6" height="1" rx="0.3" fill="#0f172a" opacity="0.4" />
      <rect x="8.3" y="10.5" width="4" height="1.4" rx="0.7" fill="#0f172a" opacity="0.55" />
    </>
  ),
  card_animated: null,

  // ------------------------------------------------------------------
  // Gallery family
  // ------------------------------------------------------------------
  gallery: (
    <>
      <defs>
        <PhotoGradient id="gallery-photo-1" />
        <PhotoGradient id="gallery-photo-2" accent />
        <PhotoGradient id="gallery-photo-3" />
      </defs>
      {/* Text column on the left */}
      <rect x="1.5" y="3.5" width="7" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="5.8" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="9.5" width="3.5" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* Vertical card track on the right — 3 stacked photo frames */}
      <rect x="13.5" y="1" width="9" height="4.2" rx="0.7" fill="url(#gallery-photo-1)" />
      <rect x="13.5" y="5.9" width="9" height="4.2" rx="0.7" fill="url(#gallery-photo-2)" />
      <rect x="13.5" y="10.8" width="9" height="4.2" rx="0.7" fill="url(#gallery-photo-3)" />
    </>
  ),
  gallery_animated: null, // shares visual with gallery
  // Visitor-driven cadence sibling of ``gallery_animated``. Same
  // composition (text left, vertical photo stack right) so the user
  // sees this is a Gallery family member at a glance — but with a
  // cursor glyph hovering over the middle photo and concentric
  // "ripple" arcs radiating from it, which is the visual shorthand
  // for "the strip reacts to where the visitor's pointer is". The
  // photos themselves are drawn slightly offset / semi-transparent
  // (mid-marquee) so the picker tile reads as "this strip is
  // moving but the cursor is what controls the speed".
  gallery_proximity: (
    <>
      <defs>
        <PhotoGradient id="gallery_proximity-1" />
        <PhotoGradient id="gallery_proximity-2" accent />
        <PhotoGradient id="gallery_proximity-3" />
      </defs>
      <rect x="1.5" y="3.5" width="7" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="5.8" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="9.5" width="3.5" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* Vertical track on the right — photos shifted slightly up so
          the strip reads as mid-scroll. The middle photo is the
          "focused" one (full opacity) because that's the card the
          cursor is closest to in the mockup. */}
      <rect x="13.5" y="0.4" width="9" height="4.2" rx="0.7" fill="url(#gallery_proximity-1)" opacity="0.7" />
      <rect x="13.5" y="5.3" width="9" height="4.2" rx="0.7" fill="url(#gallery_proximity-2)" />
      <rect x="13.5" y="10.2" width="9" height="4.2" rx="0.7" fill="url(#gallery_proximity-3)" opacity="0.7" />
      {/* Concentric arcs centred on the cursor — visual shorthand
          for "field of influence". Drawn with low opacity so they
          read as a soft halo rather than competing with the photo
          gradients. */}
      <circle cx="11.5" cy="7.4" r="2.4" fill="none" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="0.18" />
      <circle cx="11.5" cy="7.4" r="3.6" fill="none" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="0.18" />
      <circle cx="11.5" cy="7.4" r="4.8" fill="none" stroke="#ffffff" strokeOpacity="0.16" strokeWidth="0.18" />
      {/* Cursor arrow over the middle card to communicate "this is
          the photo the visitor is approaching, this is why the strip
          is slowing here". */}
      <path
        d="M 11.5 6.5 L 11.5 9.5 L 12.4 8.6 L 13.1 10 L 13.7 9.7 L 13 8.3 L 14.2 8.3 Z"
        fill="#ffffff"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="0.15"
      />
    </>
  ),
  gallery_strip: (
    <>
      <defs>
        <PhotoGradient id="gallery_strip-1" />
        <PhotoGradient id="gallery_strip-2" accent />
        <PhotoGradient id="gallery_strip-3" />
        <PhotoGradient id="gallery_strip-4" accent />
      </defs>
      {/* Horizontal card strip across the top — fourth card peeks off
          the right edge to convey the marquee identity. */}
      <rect x="0.6" y="1.2" width="5" height="6" rx="0.7" fill="url(#gallery_strip-1)" />
      <rect x="6.1" y="1.2" width="5" height="6" rx="0.7" fill="url(#gallery_strip-2)" />
      <rect x="11.6" y="1.2" width="5" height="6" rx="0.7" fill="url(#gallery_strip-3)" />
      <rect x="17.1" y="1.2" width="5" height="6" rx="0.7" fill="url(#gallery_strip-4)" opacity="0.6" />
      {/* Text below the strip */}
      <rect x="1.5" y="9" width="9" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.2" width="13" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="13.5" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Photo Banner family — three motion siblings sharing one layout
  // ------------------------------------------------------------------
  // The layout (4 equal-width frames at the top, text + CTA below)
  // is identical across the family. The pan and still variants share
  // the same static four-frame mockup — the motion story is conveyed
  // at runtime (and via the "Animated" chip on the tile) rather than
  // baked into the preview glyphs, which kept reading as confusing
  // up/down arrows inside each photo. The focus variant still earns
  // its own preview because its layout *itself* differs (one frame
  // visibly wider than the others).
  photo_banner: (
    <>
      <defs>
        <PhotoGradient id="photo_banner-1" />
        <PhotoGradient id="photo_banner-2" accent />
        <PhotoGradient id="photo_banner-3" />
        <PhotoGradient id="photo_banner-4" accent />
      </defs>
      {/* Four equal-width photo frames dominate the top */}
      <rect x="0.6" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner-1)" />
      <rect x="6.4" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner-2)" />
      <rect x="12.2" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner-3)" />
      <rect x="18" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner-4)" />
      {/* Text beneath */}
      <rect x="1.5" y="9.6" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.8" width="15" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="14" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  photo_banner_static: (
    <>
      <defs>
        <PhotoGradient id="photo_banner_static-1" />
        <PhotoGradient id="photo_banner_static-2" accent />
        <PhotoGradient id="photo_banner_static-3" />
        <PhotoGradient id="photo_banner_static-4" accent />
      </defs>
      {/* Same four-frame layout — no motion glyphs, communicating
          the "still" identity by what's *not* there. */}
      <rect x="0.6" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner_static-1)" />
      <rect x="6.4" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner_static-2)" />
      <rect x="12.2" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner_static-3)" />
      <rect x="18" y="1.2" width="5.4" height="6.5" rx="0.7" fill="url(#photo_banner_static-4)" />
      <rect x="1.5" y="9.6" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.8" width="15" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="14" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  photo_banner_focus: (
    <>
      <defs>
        <PhotoGradient id="photo_banner_focus-1" />
        <PhotoGradient id="photo_banner_focus-2" accent />
        <PhotoGradient id="photo_banner_focus-3" />
        <PhotoGradient id="photo_banner_focus-4" accent />
      </defs>
      {/* Frame 2 is captured mid-"focus" — ~50% wider than the
          others, with siblings shrunk symmetrically to make room.
          This is the key visual: at any instant of the rotating
          spotlight one frame is wider than the rest. The full strip
          width (24) and frame heights stay constant; only the
          per-frame widths breathe. */}
      <rect x="0.6" y="1.2" width="3.7" height="6.5" rx="0.7" fill="url(#photo_banner_focus-1)" opacity="0.85" />
      <rect x="4.7" y="1.2" width="9.5" height="6.5" rx="0.7" fill="url(#photo_banner_focus-2)" />
      <rect x="14.6" y="1.2" width="3.7" height="6.5" rx="0.7" fill="url(#photo_banner_focus-3)" opacity="0.85" />
      <rect x="18.7" y="1.2" width="4.7" height="6.5" rx="0.7" fill="url(#photo_banner_focus-4)" opacity="0.85" />
      {/* Subtle outline around the in-focus frame to reinforce that
          it's the one currently being highlighted by the rotation. */}
      <rect
        x="4.7"
        y="1.2"
        width="9.5"
        height="6.5"
        rx="0.7"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.35"
        strokeWidth="0.25"
      />
      <rect x="1.5" y="9.6" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.8" width="15" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="14" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  photo_banner_hover_focus: (
    <>
      <defs>
        <PhotoGradient id="photo_banner_hover_focus-1" />
        <PhotoGradient id="photo_banner_hover_focus-2" accent />
        <PhotoGradient id="photo_banner_hover_focus-3" />
        <PhotoGradient id="photo_banner_hover_focus-4" accent />
      </defs>
      {/* Same width-breathing visual as ``photo_banner_focus``
          (one frame wider, the others shrunk to make room) — but
          with a small cursor glyph hovering over the wide frame
          so the picker tile communicates "this is the visitor-
          driven sibling": the wide frame is wide BECAUSE the
          visitor is hovering it, not because a rotation is
          mid-cycle. The cursor uses the same currentColor pattern
          the text bars use so it inherits the picker's foreground. */}
      <rect x="0.6" y="1.2" width="3.7" height="6.5" rx="0.7" fill="url(#photo_banner_hover_focus-1)" opacity="0.85" />
      <rect x="4.7" y="1.2" width="9.5" height="6.5" rx="0.7" fill="url(#photo_banner_hover_focus-2)" />
      <rect x="14.6" y="1.2" width="3.7" height="6.5" rx="0.7" fill="url(#photo_banner_hover_focus-3)" opacity="0.85" />
      <rect x="18.7" y="1.2" width="4.7" height="6.5" rx="0.7" fill="url(#photo_banner_hover_focus-4)" opacity="0.85" />
      <rect
        x="4.7"
        y="1.2"
        width="9.5"
        height="6.5"
        rx="0.7"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.45"
        strokeWidth="0.3"
      />
      {/* Cursor arrow — small, positioned over the wide frame's
          interior. Drawn as a polygon to keep the SVG self-
          contained (no font icon dependency). */}
      <path
        d="M 9 4 L 9 7 L 9.9 6.1 L 10.6 7.5 L 11.2 7.2 L 10.5 5.8 L 11.7 5.8 Z"
        fill="#ffffff"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="0.15"
      />
      <rect x="1.5" y="9.6" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.8" width="15" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="14" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Mosaic family — collage with motion variants (drift / static / shuffle)
  // ------------------------------------------------------------------
  mosaic: (
    <>
      <defs>
        <PhotoGradient id="mosaic-1" />
        <PhotoGradient id="mosaic-2" accent />
        <PhotoGradient id="mosaic-3" accent />
        <PhotoGradient id="mosaic-4" />
      </defs>
      {/* 2×2 collage on the left */}
      <rect x="0.6" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic-1)" />
      <rect x="6.6" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic-2)" />
      <rect x="0.6" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic-3)" />
      <rect x="6.6" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic-4)" />
      {/* Text column on the right */}
      <rect x="14" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="14" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="14" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="14" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  mosaic_static: (
    <>
      <defs>
        <PhotoGradient id="mosaic_static-1" />
        <PhotoGradient id="mosaic_static-2" accent />
        <PhotoGradient id="mosaic_static-3" accent />
        <PhotoGradient id="mosaic_static-4" />
      </defs>
      {/* Same 2×2 collage — no motion glyphs, communicating the
          "still" identity by what's *not* there (mirrors the
          ``photo_banner_static`` preview convention). */}
      <rect x="0.6" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static-1)" />
      <rect x="6.6" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static-2)" />
      <rect x="0.6" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static-3)" />
      <rect x="6.6" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static-4)" />
      <rect x="14" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="14" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="14" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="14" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  mosaic_shuffle: (
    <>
      <defs>
        <PhotoGradient id="mosaic_shuffle-1" />
        <PhotoGradient id="mosaic_shuffle-2" accent />
        <PhotoGradient id="mosaic_shuffle-3" accent />
        <PhotoGradient id="mosaic_shuffle-4" />
      </defs>
      {/* Same 2×2 grid as the canonical Mosaic — but two of the
          tiles are caught mid-swap: the top-right tile is leaving
          its cell through the top edge (clipped by the cell), and
          the bottom-left tile is leaving through the bottom edge.
          The opposite-direction exits are the visual signature of
          the pair-swap rotation (one up, one down → they trade
          places). */}
      <rect x="0.6" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle-1)" />
      {/* Tile 2 (top-right): photo translated up out of its cell.
          We render only the visible bottom slice so the preview
          reads as "the tile is mid-exit out the top". */}
      <g clipPath="url(#mosaic_shuffle-clip-tr)">
        <rect x="6.6" y="-2.5" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle-2)" opacity="0.7" />
      </g>
      {/* Empty cell outline showing where the tile is leaving. */}
      <rect
        x="6.6"
        y="1.2"
        width="5.5"
        height="6.5"
        rx="0.6"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="0.15"
        strokeDasharray="0.5 0.4"
      />
      {/* Tile 3 (bottom-left): photo translated down out of its
          cell — opposite direction from tile 2 to reinforce the
          "pair-swap" identity. */}
      <g clipPath="url(#mosaic_shuffle-clip-bl)">
        <rect x="0.6" y="11.5" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle-3)" opacity="0.7" />
      </g>
      <rect
        x="0.6"
        y="8.2"
        width="5.5"
        height="6.5"
        rx="0.6"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="0.15"
        strokeDasharray="0.5 0.4"
      />
      <rect x="6.6" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle-4)" />
      {/* Clip paths confine the half-photos to their respective
          tile cells so they read as "leaving the cell" rather than
          "floating over the layout". */}
      <defs>
        <clipPath id="mosaic_shuffle-clip-tr">
          <rect x="6.6" y="1.2" width="5.5" height="6.5" rx="0.6" />
        </clipPath>
        <clipPath id="mosaic_shuffle-clip-bl">
          <rect x="0.6" y="8.2" width="5.5" height="6.5" rx="0.6" />
        </clipPath>
      </defs>
      {/* Text column on the right (unchanged across the family). */}
      <rect x="14" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="14" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="14" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="14" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Mosaic family — right-anchored spatial mirrors
  // ------------------------------------------------------------------
  //
  // Each mirror flips the canonical 2×2 collage from x=0.6-12.1
  // (left half) to x=11.9-23.4 (right half) and moves the text
  // column from x=14-22 (right) to x=2-10 (left). The text bars
  // are right-aligned WITHIN their column to mirror the
  // renderer's inner-edge anchoring rule (text now hugs the right
  // side of its left column, where the photos sit). Flush
  // variants (``mosaic_static_flush_right`` /
  // ``mosaic_shuffle_flush_right``) are intentionally NOT
  // registered as separate renderers — ``resolveBaseModeId``
  // strips the embedded ``_flush_`` from ``_flush_right`` so they
  // alias to their non-flush right siblings, matching how the
  // canonical ``_flush`` variants alias to their non-flush
  // canonical siblings (the chrome difference is too fine-
  // grained to read at 24×16).
  mosaic_right: (
    <>
      <defs>
        <PhotoGradient id="mosaic_right-1" />
        <PhotoGradient id="mosaic_right-2" accent />
        <PhotoGradient id="mosaic_right-3" accent />
        <PhotoGradient id="mosaic_right-4" />
      </defs>
      {/* Text column on the LEFT, right-aligned within its column
          so it visually leans toward the photo seam (inner-edge
          anchoring rule — see backend §13b). The text rects
          stretch from inside the column edge out to x=10 (the
          inner seam between text and photos). Top text bar
          longest; subsequent shorter, mirroring the right-
          anchored Magazine convention. */}
      <rect x="2" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="3" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="6.2" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* 2×2 collage on the RIGHT (mirror of the canonical mosaic
          which sits on the left). Same dimensions / corner radii
          / accent pattern so the two read as members of the
          same family at picker-thumb size. */}
      <rect x="11.9" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_right-1)" />
      <rect x="17.9" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_right-2)" />
      <rect x="11.9" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_right-3)" />
      <rect x="17.9" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_right-4)" />
    </>
  ),
  mosaic_static_right: (
    <>
      <defs>
        <PhotoGradient id="mosaic_static_right-1" />
        <PhotoGradient id="mosaic_static_right-2" accent />
        <PhotoGradient id="mosaic_static_right-3" accent />
        <PhotoGradient id="mosaic_static_right-4" />
      </defs>
      {/* Same right-anchored mirror as ``mosaic_right`` — no
          motion glyphs, communicating the "still" identity by
          what's *not* there (mirrors how ``mosaic_static`` reads
          against ``mosaic`` on the canonical side). */}
      <rect x="2" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="3" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="6.2" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      <rect x="11.9" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static_right-1)" />
      <rect x="17.9" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static_right-2)" />
      <rect x="11.9" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static_right-3)" />
      <rect x="17.9" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_static_right-4)" />
    </>
  ),
  mosaic_shuffle_right: (
    <>
      <defs>
        <PhotoGradient id="mosaic_shuffle_right-1" />
        <PhotoGradient id="mosaic_shuffle_right-2" accent />
        <PhotoGradient id="mosaic_shuffle_right-3" accent />
        <PhotoGradient id="mosaic_shuffle_right-4" />
      </defs>
      {/* Text column on the LEFT (right-aligned), grid on the
          right with the same pair-swap mid-rotation hint as the
          canonical ``mosaic_shuffle`` preview — but on the
          mirrored half. Top-LEFT tile of the grid (which is now
          x=11.9 on the right side) exits through the top;
          bottom-RIGHT tile of the grid exits through the bottom.
          Opposite-direction exits are the visual signature of
          the pair-swap rotation that makes "shuffle" read
          differently from "drift" or "static" at thumbnail
          scale. */}
      <rect x="2" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="3" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="6.2" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* Top-left tile of the right-side grid: exiting upward.
          Empty cell outline beneath shows where it's leaving. */}
      <g clipPath="url(#mosaic_shuffle_right-clip-tl)">
        <rect x="11.9" y="-2.5" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle_right-1)" opacity="0.7" />
      </g>
      <rect
        x="11.9"
        y="1.2"
        width="5.5"
        height="6.5"
        rx="0.6"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="0.15"
        strokeDasharray="0.5 0.4"
      />
      {/* Top-right tile (stationary). */}
      <rect x="17.9" y="1.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle_right-2)" />
      {/* Bottom-left tile (stationary). */}
      <rect x="11.9" y="8.2" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle_right-3)" />
      {/* Bottom-right tile of the right-side grid: exiting
          downward (opposite direction from the top-left tile). */}
      <g clipPath="url(#mosaic_shuffle_right-clip-br)">
        <rect x="17.9" y="11.5" width="5.5" height="6.5" rx="0.6" fill="url(#mosaic_shuffle_right-4)" opacity="0.7" />
      </g>
      <rect
        x="17.9"
        y="8.2"
        width="5.5"
        height="6.5"
        rx="0.6"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="0.15"
        strokeDasharray="0.5 0.4"
      />
      <defs>
        <clipPath id="mosaic_shuffle_right-clip-tl">
          <rect x="11.9" y="1.2" width="5.5" height="6.5" rx="0.6" />
        </clipPath>
        <clipPath id="mosaic_shuffle_right-clip-br">
          <rect x="17.9" y="8.2" width="5.5" height="6.5" rx="0.6" />
        </clipPath>
      </defs>
    </>
  ),

  // ------------------------------------------------------------------
  // Magazine family — editorial composition with title/photo on either
  // side; ``_right`` siblings are pure spatial mirrors of the canonical
  // pair so the picker can advertise both compositional flips.
  // ------------------------------------------------------------------
  magazine: (
    <>
      <defs>
        <PhotoGradient id="magazine-photo" />
      </defs>
      {/* Editorial title block on the left (oversized) */}
      <rect x="1.5" y="3" width="11" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6.4" width="9" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      {/* Subtitle */}
      <rect x="1.5" y="10" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      {/* CTA */}
      <rect x="1.5" y="12" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* Photo column on the right (taller, narrower than full-bleed) */}
      <rect x="14.5" y="1.5" width="8" height="13" rx="0.7" fill="url(#magazine-photo)" />
    </>
  ),
  magazine_right: (
    <>
      <defs>
        <PhotoGradient id="magazine_right-photo" />
      </defs>
      {/* Mirror of ``magazine``: photo column slides to the LEFT and
          the editorial title block + subtitle + CTA move to the
          RIGHT. Same heights / corner radii / opacities as the
          canonical preview so the two read as members of the same
          family at picker-thumb size. The text rects are
          right-aligned to their column (longer rect on top, shorter
          beneath) to mirror how the actual renderer anchors text to
          ``middle-right`` for the right-anchored variant. */}
      <rect x="1.5" y="1.5" width="8" height="13" rx="0.7" fill="url(#magazine_right-photo)" />
      {/* Oversized title block on the right, right-aligned within
          its column (column is roughly x=11.5 → x=22.5, width 11). */}
      <rect x="11.5" y="3" width="11" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="13.5" y="6.4" width="9" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      {/* Subtitle (shorter, right-aligned) */}
      <rect x="16.5" y="10" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      {/* CTA pill anchored to the right of the text column */}
      <rect x="18.7" y="12" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  magazine_swap: (
    <>
      <defs>
        <PhotoGradient id="magazine_swap-photo-front" />
        <PhotoGradient id="magazine_swap-photo-back" accent />
      </defs>
      <rect x="1.5" y="3" width="11" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6.4" width="9" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="10" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="12" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
      {/* Two stacked photo frames hint at the rotation queue. The back
          frame is dimmer + offset to read as "next up sliding in". */}
      <rect x="14.7" y="2.4" width="8" height="13" rx="0.7" fill="url(#magazine_swap-photo-back)" opacity="0.55" />
      <rect x="14.2" y="1.5" width="8" height="13" rx="0.7" fill="url(#magazine_swap-photo-front)" />
    </>
  ),
  magazine_swap_right: (
    <>
      <defs>
        <PhotoGradient id="magazine_swap_right-photo-front" />
        <PhotoGradient id="magazine_swap_right-photo-back" accent />
      </defs>
      {/* Mirror of ``magazine_swap``: rotating photo stack moves to
          the LEFT, editorial title column moves to the RIGHT. The
          back frame is offset opposite the canonical version (its
          shoulder pokes to the LEFT/below now that the stack lives
          on the left half) so the rotation hint reads visually
          consistent with the mirrored layout — the "next up"
          sliver should peek out away from the centre, not toward
          it. Same right-alignment of text rects as
          ``magazine_right``. */}
      <rect x="1.8" y="2.4" width="8" height="13" rx="0.7" fill="url(#magazine_swap_right-photo-back)" opacity="0.55" />
      <rect x="1.3" y="1.5" width="8" height="13" rx="0.7" fill="url(#magazine_swap_right-photo-front)" />
      <rect x="11.5" y="3" width="11" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="13.5" y="6.4" width="9" height="2.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="16.5" y="10" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="18.7" y="12" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Spotlight family — featured-photo-with-rotation hero. Two layout
  // siblings ship: ``spotlight_swap`` (canonical horizontal split,
  // photo beside text + thumbnail strip) and ``spotlight_swap_stack``
  // (photo on top full-width, text + clickable dot indicator
  // stacked beneath). The previously-shipped static ``spotlight``
  // mode (centered text + radial glow) was retired and remapped to
  // ``centered`` by migration
  // ``0032_remove_spotlight_mode_remap_to_centered`` — see the §12
  // retirement block in the backend ``definitions.py`` for the
  // rationale; no preview is registered for ``spotlight`` because
  // there's no picker entry for it any more.
  // ------------------------------------------------------------------
  spotlight_swap: (
    <>
      <defs>
        <PhotoGradient id="spotlight_swap-feature" />
      </defs>
      {/* Featured photo on the left */}
      <rect x="0.8" y="1" width="9" height="9.5" rx="0.8" fill="url(#spotlight_swap-feature)" />
      {/* Thumbnail strip beneath — first thumb is "active" (brighter) */}
      <rect x="0.8" y="11.4" width="2.5" height="2.5" rx="0.4" fill={IMG_GRADIENT_TOP} opacity="0.95" />
      <rect x="3.7" y="11.4" width="2.5" height="2.5" rx="0.4" fill={IMG_GRADIENT_BOTTOM} opacity="0.45" />
      <rect x="6.6" y="11.4" width="2.5" height="2.5" rx="0.4" fill={IMG_ACCENT_BOTTOM} opacity="0.45" />
      {/* Text column on the right */}
      <rect x="12.5" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="12.5" y="5.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="12.5" y="7.4" width="5" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="12.5" y="10" width="3.8" height="1.4" rx="0.7" fill="currentColor" opacity="0.65" />
    </>
  ),
  spotlight_swap_stack: (
    <>
      <defs>
        <PhotoGradient id="spotlight_swap_stack-feature" />
      </defs>
      {/* Featured photo on top, spanning the full width — mirrors
          the renderer's vertical-stack layout where the photo
          claims ~60% of the hero height across the full width.
          Borders match other photo previews so the family's
          "framed photo" identity stays consistent across the
          stack/horizontal pair. */}
      <rect x="0.8" y="0.8" width="22.4" height="8" rx="0.8" fill="url(#spotlight_swap_stack-feature)" />
      {/* Centred text column beneath the photo. The renderer
          centres heading / body / CTA in the stack layout (the
          backend's ``item_field_overrides`` flips anchors from
          ``middle-left`` to ``middle-center`` for this variant);
          mirroring that choice here keeps the picker thumbnail
          honest about what the layout actually produces. */}
      <rect x="6" y="9.6" width="12" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="7" y="11.7" width="10" height="0.9" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="10" y="13" width="4" height="1.3" rx="0.65" fill="currentColor" opacity="0.7" />
      {/* Dot indicator at the very bottom — three dots, the
          first one "active" (brighter, slightly larger) to
          signal the click-to-navigate affordance the renderer
          exposes for this variant. Even at 24×16 the dot row
          is the clearest visual difference between this and
          ``spotlight_swap`` (which shows a thumbnail strip
          beneath its left-hand photo), so it earns a spot in
          the thumbnail rather than being elided. */}
      <circle cx="11" cy="15.2" r="0.45" fill="currentColor" opacity="0.95" />
      <circle cx="12" cy="15.2" r="0.32" fill="currentColor" opacity="0.45" />
      <circle cx="13" cy="15.2" r="0.32" fill="currentColor" opacity="0.45" />
    </>
  ),

  // ------------------------------------------------------------------
  // Profile — circular portrait + intro text
  // ------------------------------------------------------------------
  profile: (
    <>
      <defs>
        <radialGradient id="profile-portrait" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={IMG_GRADIENT_TOP} stopOpacity="0.95" />
          <stop offset="100%" stopColor={IMG_GRADIENT_BOTTOM} stopOpacity="0.95" />
        </radialGradient>
        <linearGradient id="profile-bg" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f1f5f9" stopOpacity="0" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="24" height="16" fill="url(#profile-bg)" />
      {/* Circular portrait at the top */}
      <circle cx="12" cy="5.2" r="2.6" fill="url(#profile-portrait)" />
      {/* Name */}
      <rect x="7.5" y="9" width="9" height="1.5" rx="0.4" fill="currentColor" opacity="0.95" />
      {/* Tagline */}
      <rect x="9" y="11" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      {/* CTA */}
      <rect x="10" y="13" width="4" height="1.4" rx="0.7" fill="currentColor" opacity="0.7" />
    </>
  ),

  // Rounded-square frame sibling of Profile. Same centered
  // composition, but the portrait reads more like a headshot card than
  // an avatar.
  profile_rounded: (
    <>
      <defs>
        <radialGradient id="profile_rounded-portrait" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={IMG_GRADIENT_TOP} stopOpacity="0.95" />
          <stop offset="100%" stopColor={IMG_GRADIENT_BOTTOM} stopOpacity="0.95" />
        </radialGradient>
        <linearGradient id="profile_rounded-bg" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f1f5f9" stopOpacity="0" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="24" height="16" fill="url(#profile_rounded-bg)" />
      <rect x="9.4" y="2.6" width="5.2" height="5.2" rx="1.1" fill="url(#profile_rounded-portrait)" />
      <rect x="7.5" y="9" width="9" height="1.5" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="9" y="11" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="10" y="13" width="4" height="1.4" rx="0.7" fill="currentColor" opacity="0.7" />
    </>
  ),

  // ------------------------------------------------------------------
  // Stack — wide image on top, text below
  // ------------------------------------------------------------------
  stack: (
    <>
      <defs>
        <PhotoGradient id="stack-photo" />
      </defs>
      {/* Wide image banner spanning the top */}
      <rect x="0.8" y="0.8" width="22.4" height="6.5" rx="0.8" fill="url(#stack-photo)" />
      {/* Subtle horizon line for depth */}
      <rect x="0.8" y="4" width="22.4" height="0.3" fill="#ffffff" opacity="0.18" />
      {/* Text stacked beneath */}
      <rect x="1.5" y="9" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="11.2" width="15" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="13" width="13" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="14.6" width="3.8" height="1.2" rx="0.6" fill="currentColor" opacity="0.65" />
    </>
  ),

  // ------------------------------------------------------------------
  // Photo Drift family — text on top, photo BACKDROP layer beneath
  // ------------------------------------------------------------------
  //
  // The runtime ``HeroPhotoDrift`` mounts the photo track as a
  // ``position: absolute; inset: 0`` backdrop layer behind the text
  // (z-index 1, between the hero ``Background`` at z-0 and the
  // foreground content at z-10), with a vertical CSS mask gradient
  // that fades the upper portion of the photo layer to transparent
  // so the user's hero background bleeds through where the text
  // sits. The photos read as scenery — part of the background
  // composition — rather than as a stacked block beneath the text.
  //
  // The previews encode that backdrop identity with three layered
  // visual cues:
  //
  //   1. **Photo backdrop** — a depth-scattered photo grid that
  //      spans the FULL 24x16 viewBox (not just the lower half).
  //      The whole grid is masked with a vertical fade so the
  //      upper rows are transparent and the lower rows are opaque
  //      — the SVG-space analogue of the runtime CSS mask. The
  //      tile background (from the picker frame's ``from-…`` ↔
  //      ``to-…`` gradient) shows through the masked-out region,
  //      mirroring how the user's hero background shows through
  //      in the live renderer.
  //   2. **Text bars on top** — title / body / CTA bars sit at
  //      ``z-index: above`` (later in the SVG paint order) in the
  //      upper portion of the tile. They visually overlap the
  //      faded top of the photo backdrop, communicating that the
  //      text is layered ON the backdrop rather than stacked
  //      ABOVE it in flow.
  //   3. **Direction arrow** — a small arrow tip in the opaque
  //      lower portion of the backdrop pointing along the drift
  //      axis (up / left / right). Same role the previous draft's
  //      chevron played; just relocated into the visible band of
  //      the masked layer so it reads as part of the photo
  //      scenery.
  //
  // The upward sibling uses a deeper top fade than the lateral
  // siblings (matching the runtime ``hero-photo-drift-viewport
  // --vertical`` vs ``--horizontal`` mask gradients) because its
  // photo column extends most of the hero height — without the
  // deeper fade the top of the column would compete with the
  // heading.

  // ``photo_drift_up`` — vertical drift. The photo backdrop is laid
  // out as TWO lanes (left + right) of stacked cells with depth
  // scatter, evoking the runtime renderer's two-column upward
  // marquee. The arrow points UP from the bottom-right.
  photo_drift_up: (
    <>
      <defs>
        <PhotoGradient id="photo_drift_up-1" />
        <PhotoGradient id="photo_drift_up-2" accent />
        <PhotoGradient id="photo_drift_up-3" />
        <PhotoGradient id="photo_drift_up-4" accent />
        {/* Vertical fade gradient driving the backdrop mask.
            Stops mirror the runtime ``--vertical`` CSS mask
            (transparent → 0.05 → 0.55 → opaque), translated into
            SVG luminance values (black = hidden, white = visible).
            The aggressive transparent band over the upper ~50% of
            the tile is what gives the text a clean backdrop and
            sells the "photos as scenery" reading. */}
        <linearGradient id="photo_drift_up-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" />
          <stop offset="35%" stopColor="#0d0d0d" />
          <stop offset="60%" stopColor="#8c8c8c" />
          <stop offset="85%" stopColor="#ffffff" />
        </linearGradient>
        <mask id="photo_drift_up-mask" maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="24" height="16" fill="url(#photo_drift_up-fade)" />
        </mask>
      </defs>
      {/* Photo backdrop layer — full-bleed, vertically masked.
          Two lanes (left x≈2, right x≈14), four staggered Y
          positions. Cell widths/heights vary across the grid to
          encode the runtime ``DEPTH_SCALES`` chain so the picker
          tile carries the depth-scattered identity at a glance.
          Group opacity 0.92 matches the runtime
          ``.hero-photo-drift-viewport--backdrop`` opacity step. */}
      <g mask="url(#photo_drift_up-mask)" opacity="0.92">
        <rect x="2.5" y="0.4" width="6.8" height="2.8" rx="0.5" fill="url(#photo_drift_up-1)" />
        <rect x="14.2" y="1.2" width="6" height="2.4" rx="0.5" fill="url(#photo_drift_up-2)" />
        <rect x="3.4" y="4.3" width="6.2" height="2.7" rx="0.5" fill="url(#photo_drift_up-3)" />
        <rect x="13.6" y="5" width="7" height="2.8" rx="0.55" fill="url(#photo_drift_up-4)" />
        <rect x="2" y="8.4" width="7.6" height="3" rx="0.6" fill="url(#photo_drift_up-2)" />
        <rect x="14.4" y="9" width="6.6" height="2.7" rx="0.55" fill="url(#photo_drift_up-1)" />
        <rect x="3" y="12.6" width="6.8" height="3" rx="0.6" fill="url(#photo_drift_up-4)" />
        <rect x="14.2" y="13" width="7.2" height="2.8" rx="0.6" fill="url(#photo_drift_up-3)" />
      </g>
      {/* Text on top — heading / body / supporting line / CTA pill.
          Sits in y∈[1.3, 8] where the backdrop is mostly faded so
          the bars read as cleanly as on a flat tile. Same
          currentColor scale Photo Banner / Stack use, so the
          family reads as part of the same image-heavy vocabulary. */}
      <rect x="1.5" y="1.3" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="3.6" width="14" height="0.9" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="1.5" y="5" width="10.5" height="0.9" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.6" width="3.8" height="1.3" rx="0.65" fill="currentColor" opacity="0.7" />
      {/* Upward arrow in the opaque lower band. Tucked into the
          bottom-right so it doesn't crowd the text and reads as
          "the photo layer is drifting up". */}
      <path
        d="M 21 13.6 L 19.6 15.2 L 20.45 15.2 L 20.45 15.7 L 21.55 15.7 L 21.55 15.2 L 22.4 15.2 Z"
        fill="#ffffff"
        opacity="0.95"
      />
    </>
  ),
  // ``photo_drift_left`` — leftward drift. Photo backdrop laid out
  // as a horizontal newsreel (two stacked rows of scattered cells)
  // so the picker tile signals the lateral flow at a glance. The
  // mask uses a slightly weaker top fade than the upward sibling
  // (matching the runtime ``--horizontal`` CSS mask) because the
  // band is shorter and a heavier fade would feel like a hard line.
  photo_drift_left: (
    <>
      <defs>
        <PhotoGradient id="photo_drift_left-1" />
        <PhotoGradient id="photo_drift_left-2" accent />
        <PhotoGradient id="photo_drift_left-3" />
        <PhotoGradient id="photo_drift_left-4" accent />
        <linearGradient id="photo_drift_left-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" />
          <stop offset="40%" stopColor="#1a1a1a" />
          <stop offset="65%" stopColor="#b3b3b3" />
          <stop offset="90%" stopColor="#ffffff" />
        </linearGradient>
        <mask id="photo_drift_left-mask" maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="24" height="16" fill="url(#photo_drift_left-fade)" />
        </mask>
      </defs>
      {/* Photo backdrop — horizontal newsreel. Top row sits high
          enough to be partially eaten by the mask (signalling the
          fade), bottom row sits in the opaque band. Cells vary in
          width so the band reads as scattered, not gridded. */}
      <g mask="url(#photo_drift_left-mask)" opacity="0.92">
        <rect x="0.4" y="6.6" width="5.4" height="2.6" rx="0.5" fill="url(#photo_drift_left-1)" />
        <rect x="6.4" y="7.2" width="4.8" height="2.4" rx="0.5" fill="url(#photo_drift_left-2)" />
        <rect x="11.8" y="6.4" width="6" height="2.8" rx="0.5" fill="url(#photo_drift_left-3)" />
        <rect x="18.4" y="7" width="5.4" height="2.5" rx="0.5" fill="url(#photo_drift_left-4)" />
        <rect x="-0.2" y="11.2" width="5.8" height="3" rx="0.55" fill="url(#photo_drift_left-3)" />
        <rect x="6.2" y="11.6" width="5" height="2.7" rx="0.5" fill="url(#photo_drift_left-4)" />
        <rect x="11.8" y="11.2" width="6.2" height="3" rx="0.55" fill="url(#photo_drift_left-1)" />
        <rect x="18.6" y="11.6" width="5.6" height="2.8" rx="0.55" fill="url(#photo_drift_left-2)" />
      </g>
      {/* Text on top — same composition as the upward sibling so
          the family identity (text on top, photos as backdrop)
          stays consistent across siblings. */}
      <rect x="1.5" y="1.3" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="3.6" width="14" height="0.9" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="1.5" y="5" width="10.5" height="0.9" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.6" width="3.8" height="1.3" rx="0.65" fill="currentColor" opacity="0.7" />
      {/* Leftward arrow in the opaque lower band, tucked over the
          right side so it points BACK toward the text — reads as
          "the photo backdrop is drifting from right to left". */}
      <path
        d="M 18.5 13.4 L 17 12.6 L 17 13.15 L 16.2 13.15 L 16.2 13.65 L 17 13.65 L 17 14.2 Z"
        fill="#ffffff"
        opacity="0.95"
      />
    </>
  ),
  // ``photo_drift_right`` — mirror of ``photo_drift_left``. Same
  // backdrop / mask / text composition; the arrow points the other
  // way so the picker can distinguish the two direction siblings
  // at a glance.
  photo_drift_right: (
    <>
      <defs>
        <PhotoGradient id="photo_drift_right-1" />
        <PhotoGradient id="photo_drift_right-2" accent />
        <PhotoGradient id="photo_drift_right-3" />
        <PhotoGradient id="photo_drift_right-4" accent />
        <linearGradient id="photo_drift_right-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" />
          <stop offset="40%" stopColor="#1a1a1a" />
          <stop offset="65%" stopColor="#b3b3b3" />
          <stop offset="90%" stopColor="#ffffff" />
        </linearGradient>
        <mask id="photo_drift_right-mask" maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="24" height="16" fill="url(#photo_drift_right-fade)" />
        </mask>
      </defs>
      <g mask="url(#photo_drift_right-mask)" opacity="0.92">
        <rect x="0.4" y="6.6" width="5.4" height="2.6" rx="0.5" fill="url(#photo_drift_right-1)" />
        <rect x="6.4" y="7.2" width="4.8" height="2.4" rx="0.5" fill="url(#photo_drift_right-2)" />
        <rect x="11.8" y="6.4" width="6" height="2.8" rx="0.5" fill="url(#photo_drift_right-3)" />
        <rect x="18.4" y="7" width="5.4" height="2.5" rx="0.5" fill="url(#photo_drift_right-4)" />
        <rect x="-0.2" y="11.2" width="5.8" height="3" rx="0.55" fill="url(#photo_drift_right-3)" />
        <rect x="6.2" y="11.6" width="5" height="2.7" rx="0.5" fill="url(#photo_drift_right-4)" />
        <rect x="11.8" y="11.2" width="6.2" height="3" rx="0.55" fill="url(#photo_drift_right-1)" />
        <rect x="18.6" y="11.6" width="5.6" height="2.8" rx="0.55" fill="url(#photo_drift_right-2)" />
      </g>
      <rect x="1.5" y="1.3" width="11" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="3.6" width="14" height="0.9" rx="0.3" fill="currentColor" opacity="0.55" />
      <rect x="1.5" y="5" width="10.5" height="0.9" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.6" width="3.8" height="1.3" rx="0.65" fill="currentColor" opacity="0.7" />
      {/* Rightward arrow — mirror of the leftward sibling. */}
      <path
        d="M 5.5 13.4 L 7 12.6 L 7 13.15 L 7.8 13.15 L 7.8 13.65 L 7 13.65 L 7 14.2 Z"
        fill="#ffffff"
        opacity="0.95"
      />
    </>
  ),

  // ------------------------------------------------------------------
  // Photo Float family — text on the left, floating cluster on the right
  // ------------------------------------------------------------------
  //
  // The three siblings share a "text on the left half, floating
  // cluster of 3-4 photos on the right half" composition; the
  // motion (per-photo staggered bob) is identical across siblings.
  // What differs is the **frame format** of each photo:
  //
  //   * ``photo_float_rounded`` — moderate corner radius (rx ≈ 0.6),
  //     soft drop shadow implied by a faint inner highlight stroke.
  //   * ``photo_float_circle``  — full circle (``<ellipse>`` /
  //     ``<circle>`` shapes).
  //   * ``photo_float_flush``   — same moderate corner radius as the
  //     rounded sibling, but no shadow / highlight stroke. Every
  //     rectangle in the family stays softly rounded so the cluster
  //     never reads as a hard, unfinished crop; flush earns its
  //     identity by dropping the chrome, not the radius.
  //   * ``photo_float_mixed``   — rounded rectangles + circles in
  //     the same cluster, with the two circles placed DIAGONAL to
  //     each other (upper-left + lower-right) so the accents read
  //     as a deliberate visual line crossing the rectangles instead
  //     of stacking on the same side.
  //
  // The cluster shape (4 cells: anchor near in upper-right, scattered
  // siblings around it at varying scales) mirrors the ``CLUSTER_LAYOUT``
  // chain in ``HeroPhotoFloat.js`` so the picker tile is a
  // recognisable miniature of the actual hero's cluster.
  photo_float_rounded: (
    <>
      <defs>
        <PhotoGradient id="photo_float_rounded-1" />
        <PhotoGradient id="photo_float_rounded-2" accent />
        <PhotoGradient id="photo_float_rounded-3" />
        <PhotoGradient id="photo_float_rounded-4" accent />
      </defs>
      {/* Text column on the left — middle-anchored heading + body
          + CTA, same currentColor scale Split / Gallery use. */}
      <rect x="1.5" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.6" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="9.2" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="11.5" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Right-side floating cluster. 4 cells laid out in the
          ``CLUSTER_LAYOUT`` chain order: anchor (cell 0) sits
          upper-right and is biggest; far (cell 1) sits lower-left
          and is smallest; mid cells fill the corners. Z-order via
          paint order (later = on top). */}
      <rect x="12.5" y="9" width="4" height="5" rx="0.6" fill="url(#photo_float_rounded-2)" />
      <rect x="20" y="8.5" width="3" height="3.7" rx="0.5" fill="url(#photo_float_rounded-3)" opacity="0.9" />
      <rect x="13" y="2" width="4" height="5" rx="0.6" fill="url(#photo_float_rounded-1)" opacity="0.9" />
      <rect x="17" y="3.5" width="5.5" height="6.5" rx="0.6" fill="url(#photo_float_rounded-4)" />
      {/* Faint highlight strokes to suggest the soft shadow / lift
          of the rounded chrome. */}
      <rect x="17" y="3.5" width="5.5" height="6.5" rx="0.6" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="0.2" />
    </>
  ),
  photo_float_circle: (
    <>
      <defs>
        <PhotoGradient id="photo_float_circle-1" />
        <PhotoGradient id="photo_float_circle-2" accent />
        <PhotoGradient id="photo_float_circle-3" />
        <PhotoGradient id="photo_float_circle-4" accent />
      </defs>
      {/* Same text column as the rounded sibling so the family
          identity is unchanged. */}
      <rect x="1.5" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.6" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="9.2" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="11.5" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Cluster of 4 circles — same positional chain as the
          rounded sibling, but each cell is a circle instead of a
          rounded rectangle. The circle sibling promises a true
          1:1 aspect (the renderer overrides ``aspect-ratio`` to
          ``1 / 1`` on the cell), so each circle's diameter is
          set to the cell's width. */}
      <circle cx="14.5" cy="11" r="2" fill="url(#photo_float_circle-2)" />
      <circle cx="21" cy="10" r="1.6" fill="url(#photo_float_circle-3)" opacity="0.9" />
      <circle cx="15" cy="4.2" r="2" fill="url(#photo_float_circle-1)" opacity="0.9" />
      <circle cx="19.7" cy="6.2" r="2.5" fill="url(#photo_float_circle-4)" />
    </>
  ),
  photo_float_flush: (
    <>
      <defs>
        <PhotoGradient id="photo_float_flush-1" />
        <PhotoGradient id="photo_float_flush-2" accent />
        <PhotoGradient id="photo_float_flush-3" />
        <PhotoGradient id="photo_float_flush-4" accent />
      </defs>
      {/* Same text column as the rounded / circle siblings. */}
      <rect x="1.5" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.6" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="9.2" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="11.5" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Same cluster shape and same moderate ``rx`` rounding as
          the rounded sibling — the Photo Float family keeps every
          rectangle softly rounded so the cluster never reads as a
          hard, unfinished crop. The "raw printed photo" finish is
          carried by the missing chrome (no shadow, no border, no
          highlight stroke), not by sharp corners. The picker tile
          communicates this by reusing the rounded cluster geometry
          while omitting the lift / highlight overlay the rounded
          sibling shows. */}
      <rect x="12.5" y="9" width="4" height="5" rx="0.6" fill="url(#photo_float_flush-2)" />
      <rect x="20" y="8.5" width="3" height="3.7" rx="0.5" fill="url(#photo_float_flush-3)" opacity="0.9" />
      <rect x="13" y="2" width="4" height="5" rx="0.6" fill="url(#photo_float_flush-1)" opacity="0.9" />
      <rect x="17" y="3.5" width="5.5" height="6.5" rx="0.6" fill="url(#photo_float_flush-4)" />
    </>
  ),
  photo_float_mixed: (
    <>
      <defs>
        <PhotoGradient id="photo_float_mixed-1" />
        <PhotoGradient id="photo_float_mixed-2" accent />
        <PhotoGradient id="photo_float_mixed-3" />
        <PhotoGradient id="photo_float_mixed-4" accent />
      </defs>
      {/* Same text column as the other Photo Float siblings — the
          family identity (text on the left, floating cluster on
          the right) is unchanged; only the per-cell chrome
          alternates. */}
      <rect x="1.5" y="3.5" width="8" height="1.6" rx="0.4" fill="currentColor" opacity="0.95" />
      <rect x="1.5" y="6" width="9" height="1" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="7.6" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="9.2" width="6" height="1" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="11.5" width="3.8" height="1.5" rx="0.75" fill="currentColor" opacity="0.65" />
      {/* Mixed cluster — the rectangular cells (cluster cells 0
          and 1 in ``HeroPhotoFloat.js``) keep the rounded-
          rectangle frame, and the circular accents (cluster
          cells 2 and 3) sit on the panel's upper-left and
          lower-right corners so the two circles form a clean
          DIAGONAL across the cluster instead of stacking on the
          same side. The diagonal pairing is the visual identity
          of the sibling; pinning it in the preview matches what
          users get in the actual hero. Paint order ascends with
          the cluster z-stack so the anchor rect (drawn last)
          sits on top of the lower-right circle where they
          overlap. */}
      <rect x="11.5" y="9.5" width="3.2" height="3.2" rx="0.5" fill="url(#photo_float_mixed-1)" opacity="0.9" />
      <circle cx="13.5" cy="5" r="2" fill="url(#photo_float_mixed-3)" opacity="0.9" />
      <circle cx="18.5" cy="11.5" r="2" fill="url(#photo_float_mixed-2)" opacity="0.95" />
      <rect x="17" y="3.5" width="5.5" height="6.5" rx="0.6" fill="url(#photo_float_mixed-4)" />
      {/* Faint highlight stroke on the anchor rect — same lift
          treatment the rounded sibling uses, so the rectangular
          half of the mixed cluster reads with the same polished
          chrome story. */}
      <rect x="17" y="3.5" width="5.5" height="6.5" rx="0.6" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="0.2" />
    </>
  ),

  // ------------------------------------------------------------------
  // Diagonal Split family — half-image / half-text with a TILTED seam
  // ------------------------------------------------------------------
  //
  // Three siblings ship:
  //   * ``diagonal_split``      — canonical 25° subtle slash. Both
  //                               photo cuts run PARALLEL leaning
  //                               RIGHT (``\\`` slope). Text gap is
  //                               a constant-width parallelogram that
  //                               shifts rightward as the eye scans
  //                               down.
  //   * ``diagonal_split_left`` — same geometry, mirrored direction.
  //                               Both cuts run PARALLEL leaning
  //                               LEFT (``//`` slope). Text gap
  //                               shifts leftward as you scan down.
  //   * ``diagonal_split_x``    — 45° corner-to-corner X-pattern.
  //                               Two right triangles in opposite
  //                               corners with the text in the
  //                               diagonal band between them.
  //
  // Each preview encodes the family's identity by drawing photo
  // polygons with the same tilted geometry the renderer uses — a
  // flat half-image rectangle (like the ``split`` preview) would
  // not communicate the diagonal cut and the user would mistake
  // the family for plain Split. We use SVG ``<polygon>`` elements
  // with points that mirror the polygon coordinates
  // ``HeroDiagonalSplit.js`` computes at runtime, so the picker
  // tile reads as a true miniature of the actual hero
  // composition.
  //
  // The two subtle-cut previews differ ONLY in slope direction —
  // the photo widths and slope amplitude are identical so the
  // picker tiles read as strict mirror images of each other. This
  // matches the runtime behaviour where flipping between the two
  // siblings only changes the cut direction, never the photo
  // dimensions.
  //
  // Both previews show the TWO-IMAGE composition (a polygon on
  // each side flanking the text column) because that's the most
  // distinctive visual identity of the family — it's what cleanly
  // separates Diagonal Split from canonical Split (which only ever
  // hosts ONE image). Users picking the family deliberately for
  // its "two photos with a tilt" identity see that promise made
  // good in the picker tile. The single-image case still works at
  // runtime — it's just not the strongest read for the picker.
  //
  // ``diagonal_split`` — canonical PARALLEL ``\\`` cuts. The
  // renderer's two-image polygons (with TWO_IMAGE_AVG_INNER=20%
  // and TWO_IMAGE_INNER_OFFSET=5%) translate into the 24×16
  // viewBox as:
  //   - left photo widest at BOTTOM  (inner edge x = 25% → 6.0)
  //   - left photo narrowest at TOP  (inner edge x = 15% → 3.6)
  //   - right photo widest at TOP    (inner edge x = 75% → 18.0)
  //   - right photo narrowest at BOT (inner edge x = 85% → 20.4)
  // Both inner edges therefore lean RIGHT as you scan top→bottom,
  // sliding the text gap rightward. The text bars are slightly
  // shifted to FOLLOW the gap's diagonal so the picker tile
  // reads as "text wedged into the parallelogram", not "text
  // centered on top of the photos".
  diagonal_split: (
    <>
      <defs>
        <PhotoGradient id="diagonal_split-photo-l" />
        <PhotoGradient id="diagonal_split-photo-r" accent />
      </defs>
      {/* Left image — ``\\`` cut: narrow at top (3.6), wide at
          bottom (6.0). */}
      <polygon
        points="0,0 3.6,0 6.0,16 0,16"
        fill="url(#diagonal_split-photo-l)"
      />
      {/* Right image — parallel ``\\`` cut: wide at top (left
          edge at 18.0), narrow at bottom (left edge at 20.4). */}
      <polygon
        points="18.0,0 24,0 24,16 20.4,16"
        fill="url(#diagonal_split-photo-r)"
      />
      {/* Text bars — slightly tilted along the gap's parallelogram
          so they appear NEXT TO the photos, not stacked on top.
          Each bar's ``x`` shifts rightward as ``y`` increases,
          mimicking the gap's lateral drift. */}
      <rect x="7.0" y="3.5" width="9" height="1.4" rx="0.35" fill="currentColor" opacity="0.95" />
      <rect x="7.7" y="6.0" width="8.0" height="0.8" rx="0.25" fill="currentColor" opacity="0.55" />
      <rect x="8.2" y="7.4" width="7.0" height="0.8" rx="0.25" fill="currentColor" opacity="0.5" />
      <rect x="9.5" y="10.6" width="3.0" height="1.2" rx="0.6" fill="currentColor" opacity="0.7" />
    </>
  ),
  // ``diagonal_split_left`` — mirror of ``diagonal_split``. Parallel
  // ``//`` cuts (both inner edges lean LEFT as you scan
  // top→bottom). Same photo widths and slope amplitude — only the
  // direction flips, so the two previews read as strict mirror
  // images side-by-side in the picker.
  diagonal_split_left: (
    <>
      <defs>
        <PhotoGradient id="diagonal_split_left-photo-l" />
        <PhotoGradient id="diagonal_split_left-photo-r" accent />
      </defs>
      {/* Left image — ``//`` cut: wide at top (6.0), narrow at
          bottom (3.6). */}
      <polygon
        points="0,0 6.0,0 3.6,16 0,16"
        fill="url(#diagonal_split_left-photo-l)"
      />
      {/* Right image — parallel ``//`` cut: narrow at top (left
          edge at 20.4), wide at bottom (left edge at 18.0). */}
      <polygon
        points="20.4,0 24,0 24,16 18.0,16"
        fill="url(#diagonal_split_left-photo-r)"
      />
      {/* Text bars shift LEFTWARD as ``y`` increases, mirroring
          the gap's leftward drift. */}
      <rect x="9.0" y="3.5" width="9" height="1.4" rx="0.35" fill="currentColor" opacity="0.95" />
      <rect x="8.3" y="6.0" width="8.0" height="0.8" rx="0.25" fill="currentColor" opacity="0.55" />
      <rect x="7.8" y="7.4" width="7.0" height="0.8" rx="0.25" fill="currentColor" opacity="0.5" />
      <rect x="11.5" y="10.6" width="3.0" height="1.2" rx="0.6" fill="currentColor" opacity="0.7" />
    </>
  ),
  diagonal_split_x: (
    <>
      <defs>
        <PhotoGradient id="diagonal_split_x-photo-tl" />
        <PhotoGradient id="diagonal_split_x-photo-br" accent />
      </defs>
      {/* Top-left triangle — corner-to-corner cut. The renderer
          uses ``buildXTwoImageTopLeftPolygon`` with a 65% inset
          so the triangle's hypotenuse runs from (65%, 0) down
          to (0, 65%) inside the hero box. In the 24×16 viewBox
          that's (~15.6, 0) and (0, ~10.4). The triangle's right
          angle sits in the top-left corner. */}
      <polygon
        points="0,0 15.6,0 0,10.4"
        fill="url(#diagonal_split_x-photo-tl)"
      />
      {/* Bottom-right triangle — mirror of the top-left.
          Hypotenuse runs from (100% - 65%, 100%) to (100%,
          100% - 65%) = (~8.4, 16) to (24, ~5.6). The right
          angle sits in the bottom-right corner. The two
          triangles' hypotenuses are parallel so the text band
          between them is a uniform-width diagonal strip. */}
      <polygon
        points="24,5.6 24,16 8.4,16"
        fill="url(#diagonal_split_x-photo-br)"
      />
      {/* Text bars in the diagonal band — tilted ~45° so they
          sit naturally inside the visible band between the two
          triangles. SVG transform rotates each rect around its
          own centre. The rects are kept short and stacked along
          the band's centreline (running from upper-right to
          lower-left of the visible band, perpendicular to the
          triangles' hypotenuses). */}
      <g transform="rotate(-33 12 8)">
        <rect x="7.5" y="5.5" width="9" height="1.4" rx="0.35" fill="currentColor" opacity="0.95" />
        <rect x="8" y="7.6" width="8" height="0.8" rx="0.25" fill="currentColor" opacity="0.5" />
        <rect x="8.5" y="9" width="7" height="0.8" rx="0.25" fill="currentColor" opacity="0.45" />
        <rect x="9.5" y="10.6" width="5" height="1.1" rx="0.55" fill="currentColor" opacity="0.65" />
      </g>
    </>
  ),
  // ------------------------------------------------------------------
  // Circle Split family — half-image / half-text with a CURVED seam
  // ------------------------------------------------------------------
  //
  // Three siblings ship: ``circle_split`` (canonical gentle inward
  // bulge), ``circle_split_deep`` (near-semicircular bulge with
  // photo-as-disc-segment reading), and ``circle_split_organic``
  // (irregular continuous wavy seam — torn-paper / hand-drawn
  // identity instead of a clean geometric arc). Each preview
  // encodes the
  // family's identity by drawing photo polygons with the same
  // curved geometry the renderer uses — a flat half-image
  // rectangle (like the ``split`` preview) would not communicate
  // the curved cut and the user would mistake the family for
  // plain Split. We use SVG ``<path>`` elements with cubic
  // Bezier coordinates that mirror what HeroCircleSplit.js
  // computes at runtime, so the picker tile reads as a true
  // miniature of the actual hero composition.
  //
  // Both previews show the TWO-IMAGE composition (a curved
  // photo on each side flanking a centred text column) because
  // it's the most distinctive visual identity of the family —
  // it's what cleanly separates Circle Split from canonical
  // Split (which only ever hosts ONE image). Users picking the
  // family deliberately for its "two photos with a curve"
  // identity see that promise made good in the picker tile.
  // The single-image case still works at runtime — it's just
  // not the strongest read for the picker. Same per-sibling-
  // thumbnail reasoning Diagonal Split / Photo Float use for
  // their cut-angle and frame-format variants.
  circle_split: (
    <>
      <defs>
        <PhotoGradient id="circle_split-photo-l" />
        <PhotoGradient id="circle_split-photo-r" accent />
      </defs>
      {/* Left image — gentle bulge on its inner (right) edge.
          Path mirrors ``buildLeftPath(SUBTLE_TWO_TOP_X = 0.40,
          SUBTLE_TWO_CTRL_X = 0.50)`` from HeroCircleSplit.js,
          scaled into the 24×16 viewBox:
            top-left:    (0, 0)
            top-right:   (24 * 0.40, 0)              = (9.6, 0)
            bezier ctrl: (24 * 0.50, 0) → (12, 0) and
                         (24 * 0.50, 16) → (12, 16)
            bottom-right: (24 * 0.40, 16)            = (9.6, 16)
            bottom-left: (0, 16)
          Reading the path:
            M 0,0     start at top-left
            L 9.6,0   line right to bezier start
            C 12,0 12,16 9.6,16   cubic bezier bulging to x=12 at midpoint
            L 0,16    line back to bottom-left
            Z         close. */}
      <path
        d="M 0,0 L 9.6,0 C 12,0 12,16 9.6,16 L 0,16 Z"
        fill="url(#circle_split-photo-l)"
      />
      {/* Right image — mirror of the left polygon. Bezier
          control at x = 24 - 12 = 12 (the bulge meets the
          left's at exactly the centre line, leaving a
          tapered text column at the centre). Anchor at x =
          24 - 9.6 = 14.4. */}
      <path
        d="M 24,0 L 14.4,0 C 12,0 12,16 14.4,16 L 24,16 Z"
        fill="url(#circle_split-photo-r)"
      />
      {/* Text column in the centre — text bars sit in the
          tapered gap between the two bulges. The bars get
          progressively narrower toward the middle of the
          column to communicate the pinching effect at the
          horizontal centre. Centred so the composition's
          visual axis stays vertical. */}
      <rect x="10.6" y="3.8" width="2.8" height="1.4" rx="0.35" fill="currentColor" opacity="0.95" />
      <rect x="10.9" y="6" width="2.2" height="0.7" rx="0.25" fill="currentColor" opacity="0.5" />
      <rect x="11.1" y="7" width="1.8" height="0.7" rx="0.25" fill="currentColor" opacity="0.45" />
      <rect x="10.7" y="10.5" width="2.6" height="1.1" rx="0.55" fill="currentColor" opacity="0.65" />
    </>
  ),
  circle_split_deep: (
    <>
      <defs>
        <PhotoGradient id="circle_split_deep-photo-l" />
        <PhotoGradient id="circle_split_deep-photo-r" accent />
      </defs>
      {/* Left image — deep bulge looking like a half-disc.
          Path mirrors ``buildLeftPath(DEEP_TWO_TOP_X = 0.25,
          DEEP_TWO_CTRL_X = 0.50)`` from HeroCircleSplit.js,
          scaled into the 24×16 viewBox:
            top-right anchor:   (24 * 0.25, 0)  = (6, 0)
            bezier control:     (24 * 0.50, 0) → (12, 0) and
                                (24 * 0.50, 16) → (12, 16)
            bottom-right anchor: (24 * 0.25, 16) = (6, 16)
          The bezier control is more pronounced relative to the
          anchor distance than in the subtle preview (the
          control x=12 is 6 units past the anchor x=6, vs.
          2.4 units past in the subtle preview), making the
          bulge read as a near-semicircle. */}
      <path
        d="M 0,0 L 6,0 C 12,0 12,16 6,16 L 0,16 Z"
        fill="url(#circle_split_deep-photo-l)"
      />
      {/* Right image — mirror of the deep left disc. Anchor
          at x = 24 - 6 = 18, control at x = 12. The two
          discs meet at the centre line, leaving an almost
          point-shaped pinch at the vertical midpoint and
          slightly opening up at the top/bottom of the hero. */}
      <path
        d="M 24,0 L 18,0 C 12,0 12,16 18,16 L 24,16 Z"
        fill="url(#circle_split_deep-photo-r)"
      />
      {/* Text bars in the pinched centre band — only short
          accents at the top and bottom of the visible band
          (where the two discs pull back). The middle of the
          column is too narrow for any meaningful text bar in
          the deep variant, so we deliberately leave it
          empty. The accent rects communicate "text lives
          here, but only in the wider top + bottom regions of
          the pinched column" — accurate to how the runtime
          composition reads. */}
      <rect x="10.8" y="2.2" width="2.4" height="1.2" rx="0.3" fill="currentColor" opacity="0.95" />
      <rect x="11.1" y="4" width="1.8" height="0.6" rx="0.2" fill="currentColor" opacity="0.5" />
      <rect x="11.1" y="11.4" width="1.8" height="0.6" rx="0.2" fill="currentColor" opacity="0.5" />
      <rect x="10.8" y="12.6" width="2.4" height="1.2" rx="0.6" fill="currentColor" opacity="0.65" />
    </>
  ),
  circle_split_organic: (
    <>
      <defs>
        <PhotoGradient id="circle_split_organic-photo-l" />
        <PhotoGradient id="circle_split_organic-photo-r" accent />
      </defs>
      {/* Left image — irregular continuous wavy inner edge.
          The path is the 24×16-scaled equivalent of the
          ``ORGANIC_TWO_SPEC`` chain in HeroCircleSplit.js
          (the same per-segment cubic Bezier descriptors, with
          x values multiplied by 24 and y values multiplied by
          16). The wave pattern is asymmetric on purpose — the
          irregularity is the family identity, not a defect.

          Spec → preview coordinates:
            topX = 0.42 → 24 * 0.42 = 10.08
            bottomX = 0.46 → 24 * 0.46 = 11.04
            seg 1: cp1 (0.55,0.05) cp2 (0.50,0.18) end (0.40,0.22)
                  → (13.2, 0.8) (12, 2.88) (9.6, 3.52)
            seg 2: cp1 (0.30,0.27) cp2 (0.34,0.42) end (0.46,0.48)
                  → (7.2, 4.32) (8.16, 6.72) (11.04, 7.68)
            seg 3: cp1 (0.58,0.54) cp2 (0.50,0.68) end (0.38,0.72)
                  → (13.92, 8.64) (12, 10.88) (9.12, 11.52)
            seg 4: cp1 (0.28,0.76) cp2 (0.36,0.92) end (0.46,1.00)
                  → (6.72, 12.16) (8.64, 14.72) (11.04, 16)

          The composite path threads four wave segments down
          the inner edge — exactly what the runtime renders,
          just at picker-tile scale. The bulge alternates
          between dent (toward x=6.72) and bulge (toward
          x=13.92) so the picker tile reads as obviously not
          circular and obviously not straight. */}
      <path
        d="M 0,0 L 10.08,0 C 13.2,0.8 12,2.88 9.6,3.52 C 7.2,4.32 8.16,6.72 11.04,7.68 C 13.92,8.64 12,10.88 9.12,11.52 C 6.72,12.16 8.64,14.72 11.04,16 L 0,16 Z"
        fill="url(#circle_split_organic-photo-l)"
      />
      {/* Right image — mirror of the organic left wave across
          x=12 (the centre of the 24-wide viewBox). Each x
          coordinate becomes ``24 - x``; y is unchanged;
          segment ordering is preserved. The result has the
          same wobble pattern mirrored, so the two photos
          read as mirror images of each other (matching
          irregularity, opposite handedness — the hand-built
          identity reinforces itself rather than competing). */}
      <path
        d="M 24,0 L 13.92,0 C 10.8,0.8 12,2.88 14.4,3.52 C 16.8,4.32 15.84,6.72 12.96,7.68 C 10.08,8.64 12,10.88 14.88,11.52 C 17.28,12.16 15.36,14.72 12.96,16 L 24,16 Z"
        fill="url(#circle_split_organic-photo-r)"
      />
      {/* Text bars in the irregular middle column. The bars
          shift slightly off the centre line to communicate
          that the surrounding seams are wavy rather than
          symmetric — the column itself isn't a perfectly
          centred slab. We keep the visual emphasis (wider /
          stronger bars) at the top and bottom of the
          column where the bulges pull back, leaving a gap
          near y≈8 where the left bulge (cp x=13.92) and the
          right dent (cp x=10.08) crowd the centre most. */}
      <rect x="10.7" y="1.6" width="2.6" height="1.2" rx="0.3" fill="currentColor" opacity="0.95" />
      <rect x="11.2" y="4.6" width="2.0" height="0.6" rx="0.2" fill="currentColor" opacity="0.55" />
      <rect x="10.9" y="6.0" width="2.4" height="0.6" rx="0.2" fill="currentColor" opacity="0.5" />
      <rect x="11.1" y="9.6" width="2.0" height="0.6" rx="0.2" fill="currentColor" opacity="0.5" />
      <rect x="10.6" y="13.0" width="2.7" height="1.2" rx="0.6" fill="currentColor" opacity="0.65" />
    </>
  ),
};

// ---------------------------------------------------------------------------
// Mode → preview resolution (handles ``_animated`` and ``_flush`` siblings)
// ---------------------------------------------------------------------------
//
// Three variant suffixes share the visual identity of their base
// preview and reuse it via the resolver:
//
//   * ``_animated`` — paired animated siblings (``card_animated``,
//     ``centered_animated``, ``gallery_animated``). The picker stamps
//     an "Animated" chip on the tile to convey the motion difference.
//   * ``_flush``    — edge-to-edge finish siblings for the photo
//     families (Gallery / Photo Banner / Mosaic — see the rationale
//     block on ``GalleryFlushMode`` in the backend definitions). The
//     composition + motion story are unchanged from the canonical
//     sibling; only the per-photo chrome differs (square corners,
//     no shadow, no inter-photo gap). The label
//     ("Gallery (Flush)" etc.) and the description carry that
//     distinction at picker-tile size; we don't ship a separate
//     thumbnail because the difference is so fine-grained at 24×16
//     that it would read as noise rather than signal.
//   * ``_animated_flush`` — combination siblings (``gallery_animated_flush``
//     today). We strip both suffixes in any order so the resolver
//     remains commutative regardless of which variant is layered
//     first; the canonical thumbnail ends up rendering for the
//     base layout in every case.
//
// Order of stripping doesn't matter for the suffixes we currently
// ship (``_animated`` and ``_flush`` aren't subsets of each other),
// but we strip in a fixed order so the function reads predictably:
// flush first (since flush variants of animated modes are spelled
// ``_animated_flush``, with flush as the trailing suffix), then
// animated. The two-pass form keeps the door open for future
// suffixes without rewriting callers.

const ANIMATED_SUFFIX = "_animated";
const FLUSH_SUFFIX = "_flush";
// Combination suffix carried by Mosaic's right-anchored flush
// mirrors (``mosaic_static_flush_right`` /
// ``mosaic_shuffle_flush_right``). We strip the embedded
// ``_flush`` part WITHOUT removing the trailing ``_right`` so the
// resolver lands on the right-anchored mirror's preview (e.g.
// ``mosaic_static_flush_right`` → ``mosaic_static_right``) instead
// of falling all the way back to the canonical left-anchored
// preview. Same fine-grained-chrome reasoning the regular
// ``_flush`` strip uses (the chrome difference is too small to
// read at 24×16 — the mirror still earns a separate thumbnail
// because the left/right flip IS legible at that scale).
const FLUSH_RIGHT_SUFFIX = "_flush_right";
const RIGHT_SUFFIX = "_right";

// Explicit alias map for variants whose visual identity at picker-
// tile size is identical to a *named* canonical sibling but whose
// id can't be derived by stripping a generic suffix. The Magazine
// family ships ``magazine_fade`` / ``magazine_fade_right`` as
// motion siblings of ``magazine_swap`` / ``magazine_swap_right``:
// the rotating-photo composition is unchanged, only the
// transition style differs (cross-fade vs. slide-up), and at
// 24×16 thumbnail scale that distinction would read as noise. We
// intentionally don't ship a separate fade thumbnail because:
//   * The label ("Magazine Fade" / "Magazine Fade Right") and the
//     description carry the motion difference at picker-tile
//     size; the SVG reuse keeps the family taxonomy visually
//     coherent (all four rotating-photo variants share their
//     compositional silhouette in the picker).
//   * Adding a tiny "fade" indicator on a thumbnail this small
//     would be more cognitive load than signal — users compare
//     the family's motion stories from labels + description, not
//     by squinting at frame chrome.
// Living next to the suffix strippers (rather than as a special
// case earlier) keeps the resolver flat: aliases run before
// suffix-based reuse so ``magazine_fade_right`` first resolves
// to ``magazine_swap_right`` and then (if needed) any future
// ``_right``-targeted suffix could continue to work uniformly.
const PREVIEW_ALIAS_MAP = {
  magazine_fade: "magazine_swap",
  magazine_fade_right: "magazine_swap_right",
};

// Mode ids whose trailing ``_flush`` is part of the mode's own
// identity (a *family-format* dimension) rather than a chrome
// finish layered on top of a canonical sibling. The Photo Float
// family ships ``photo_float_rounded`` / ``photo_float_circle``
// / ``photo_float_flush`` as three frame-format siblings — none
// of them is "the canonical Photo Float with flush chrome
// applied"; ``photo_float_flush`` IS the raw-printed-photo
// sibling. Stripping its ``_flush`` would land on the
// non-existent ``photo_float`` id and the picker would fall
// through to the monogram. Listing it here exempts it from the
// generic ``FLUSH_SUFFIX`` strip below so its dedicated SVG
// preview renders. Same reasoning as ``PREVIEW_ALIAS_MAP`` —
// the resolver is suffix-driven by default but recognises a
// small set of self-aliasing exceptions where the suffix is
// load-bearing for the identity. Add to this set whenever a new
// mode ships a ``_flush`` suffix that's not a chrome flip on a
// pre-existing canonical sibling.
const FLUSH_SUFFIX_PRESERVE = new Set(["photo_float_flush"]);

export function resolveBaseModeId(modeId) {
  if (!modeId) return modeId;
  let baseId = modeId;
  // Apply explicit aliases first. Done before suffix stripping so
  // a future "alias + suffix" combination (e.g. a hypothetical
  // ``magazine_fade_animated``) still resolves predictably: the
  // alias maps the family identity, then the suffix stripper
  // walks back to the base preview the same way it does for the
  // canonical id.
  if (PREVIEW_ALIAS_MAP[baseId]) {
    baseId = PREVIEW_ALIAS_MAP[baseId];
  }
  // Combination ``_flush_right`` strip: peel the embedded
  // ``_flush`` while preserving the trailing ``_right`` so the
  // right-anchored flush mirrors fall through to their
  // non-flush right siblings (which DO have dedicated
  // thumbnails). Order matters here — done BEFORE the bare
  // ``_flush`` strip so e.g. ``mosaic_static_flush_right`` lands
  // on ``mosaic_static_right`` (not ``mosaic_static`` then
  // ``_right`` in some accidental tail). See the
  // ``FLUSH_RIGHT_SUFFIX`` declaration for the rationale.
  if (baseId.endsWith(FLUSH_RIGHT_SUFFIX)) {
    baseId = baseId.slice(0, -FLUSH_RIGHT_SUFFIX.length) + RIGHT_SUFFIX;
  }
  if (
    baseId.endsWith(FLUSH_SUFFIX) &&
    !FLUSH_SUFFIX_PRESERVE.has(baseId)
  ) {
    baseId = baseId.slice(0, -FLUSH_SUFFIX.length);
  }
  if (baseId.endsWith(ANIMATED_SUFFIX)) {
    baseId = baseId.slice(0, -ANIMATED_SUFFIX.length);
  }
  return baseId;
}

/**
 * The actual preview component used by the picker.
 *
 * Renders an SVG sized to fill its parent. The picker tile supplies
 * the wrapper border + background; this component owns only the
 * inner illustration. Falls back to a single-letter monogram when no
 * preview is registered for the mode id (so the picker doesn't
 * render a blank tile while a new mode is being wired up).
 */
const HeroModePreview = React.memo(({ modeId, isSelected = false, className = "" }) => {
  const baseId = resolveBaseModeId(modeId);
  const renderer = PREVIEW_RENDERERS[baseId];

  if (!renderer) {
    return (
      <div
        className={`w-full h-full flex items-center justify-center ${className}`}
        data-testid={`hero-mode-preview-fallback-${baseId}`}
      >
        <span
          className={`text-xs font-semibold ${
            isSelected ? "text-white/70" : textColors.muted
          }`}
        >
          {(baseId || "?").charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <svg
      viewBox="0 0 24 16"
      className={`w-full h-full ${
        isSelected ? "text-white" : textColors.secondary
      } ${className}`}
      preserveAspectRatio="xMidYMid slice"
      data-testid={`hero-mode-preview-${baseId}`}
      aria-hidden="true"
    >
      {renderer}
    </svg>
  );
});
HeroModePreview.displayName = "HeroModePreview";

export default HeroModePreview;
